'use client';

import { c as _c } from "react/compiler-runtime";
import { ReactNode } from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { LazyImage } from '@/components/LazyImage';
interface BreadcrumbItem {
  label: string;
  href?: string;
}
interface HeroAction {
  label: string;
  href: string;
  variant?: 'primary' | 'secondary';
}
interface SubCategory {
  title: string;
  description: string;
}
interface ThemePageProps {
  /** Breadcrumb navigation items */
  breadcrumbs?: BreadcrumbItem[];
  /** Small label above the heading (e.g. "THEMES") */
  eyebrow?: string;
  /** Main heading */
  title: string;
  /** Description paragraph below the heading */
  description?: string;
  /** Hero image URL */
  heroImage?: string;
  /** CTA buttons in the hero */
  actions?: HeroAction[];
  /** Sub-categories section title */
  subCategoriesTitle?: string;
  /** Sub-categories section subtitle */
  subCategoriesSubtitle?: string;
  /** Sub-category items */
  subCategories?: SubCategory[];
  /** Child sections (ThemeInitiatives, ThemeProviders, etc.) */
  children: ReactNode;
}
export function ThemePage(t0) {
  const $ = _c(32);
  const {
    breadcrumbs,
    eyebrow,
    title,
    description,
    heroImage,
    actions,
    subCategoriesTitle,
    subCategoriesSubtitle,
    subCategories,
    children
  } = t0;
  let t1;
  if ($[0] !== breadcrumbs) {
    t1 = breadcrumbs && breadcrumbs.length > 0 && <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-6">{breadcrumbs.map(_temp)}</nav>;
    $[0] = breadcrumbs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== eyebrow) {
    t2 = eyebrow && <span className="text-xs font-semibold tracking-widest text-indigo-600 uppercase mb-3 block">{eyebrow}</span>;
    $[2] = eyebrow;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== title) {
    t3 = <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">{title}</h1>;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== description) {
    t4 = description && <p className="mt-4 text-lg text-gray-600 leading-relaxed max-w-xl">{description}</p>;
    $[6] = description;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== actions) {
    t5 = actions && actions.length > 0 && <div className="mt-6 flex flex-wrap gap-3">{actions.map(_temp2)}</div>;
    $[8] = actions;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div>{t2}{t3}{t4}{t5}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== heroImage || $[16] !== title) {
    t7 = heroImage && <div className="relative"><div className="rounded-2xl overflow-hidden shadow-xl"><LazyImage src={heroImage} alt={title} className="w-full h-[300px] sm:h-[400px]" objectFit="cover" /></div></div>;
    $[15] = heroImage;
    $[16] = title;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 lg:gap-12 items-center">{t6}{t7}</div>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t1 || $[22] !== t8) {
    t9 = <section className="relative overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-purple-50"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-8">{t1}{t8}</div></section>;
    $[21] = t1;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== subCategories || $[25] !== subCategoriesSubtitle || $[26] !== subCategoriesTitle) {
    t10 = subCategories && subCategories.length > 0 && <section className="py-12 sm:py-16 border-b border-gray-100"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{subCategoriesTitle && <div className="mb-8"><h3 className="text-2xl sm:text-3xl font-bold text-gray-900">{subCategoriesTitle}</h3>{subCategoriesSubtitle && <p className="mt-2 text-gray-600">{subCategoriesSubtitle}</p>}</div>}<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{subCategories.map(_temp3)}</div></div></section>;
    $[24] = subCategories;
    $[25] = subCategoriesSubtitle;
    $[26] = subCategoriesTitle;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== children || $[29] !== t10 || $[30] !== t9) {
    t11 = <div className="min-h-screen bg-white">{t9}{t10}{children}</div>;
    $[28] = children;
    $[29] = t10;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}
function _temp3(category, index_1) {
  return <div key={index_1} className="bg-gray-50 rounded-xl p-5 border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors"><h4 className="font-semibold text-gray-900 mb-1.5">{category.title}</h4><p className="text-sm text-gray-600 leading-relaxed">{category.description}</p></div>;
}
function _temp2(action, index_0) {
  return <Link key={index_0} href={action.href} className={`inline-flex items-center px-5 py-2.5 rounded-full font-medium text-sm transition-colors cursor-pointer ${action.variant === "secondary" ? "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}>{action.label}</Link>;
}
function _temp(crumb, index) {
  return <span key={index} className="flex items-center gap-1.5">{index > 0 && <ChevronRight className="w-3.5 h-3.5" />}{crumb.href ? <Link href={crumb.href} className="hover:text-indigo-600 transition-colors cursor-pointer">{crumb.label}</Link> : <span className="text-gray-900 font-medium">{crumb.label}</span>}</span>;
}